import { useState } from 'react';
import { Download, FileSpreadsheet, FileText, X } from 'lucide-react';

interface ExportButtonProps {
  onExportCSV: () => void | Promise<void>;
  onExportExcel: () => void | Promise<void>;
  label?: string;
  variant?: 'default' | 'compact';
}

export function ExportButton({ 
  onExportCSV, 
  onExportExcel, 
  label = 'Export',
  variant = 'default'
}: ExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleExport = (type: 'csv' | 'excel') => {
    if (type === 'csv') {
      onExportCSV();
    } else {
      onExportExcel();
    }
    setIsOpen(false);
  };

  if (variant === 'compact') {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold text-sm transition-all shadow-lg hover:shadow-xl"
        >
          <Download className="w-4 h-4" />
          <span>{label}</span>
        </button>

        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
              <button
                onClick={() => handleExport('csv')}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
              >
                <FileText className="w-4 h-4 text-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">CSV</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Comma-separated</p>
                </div>
              </button>
              
              <button
                onClick={() => handleExport('excel')}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left border-t border-slate-200 dark:border-slate-700"
              >
                <FileSpreadsheet className="w-4 h-4 text-green-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Excel</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">XLSX format</p>
                </div>
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Default variant - Modal style
  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold transition-all shadow-lg hover:shadow-xl"
      >
        <Download className="w-5 h-5" />
        <span>{label}</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                Export Data
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              Choose your preferred export format:
            </p>

            <div className="space-y-3">
              <button
                onClick={() => handleExport('csv')}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-2 border-transparent hover:border-blue-500 transition-all group"
              >
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900 dark:text-slate-100">CSV Format</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Comma-separated values
                  </p>
                </div>
              </button>

              <button
                onClick={() => handleExport('excel')}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-700/50 hover:bg-green-50 dark:hover:bg-green-900/20 border-2 border-transparent hover:border-green-500 transition-all group"
              >
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileSpreadsheet className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900 dark:text-slate-100">Excel Format</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    XLSX spreadsheet
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}